export interface PayDisplay {
  amount: string;
  suffix: string;
  label: string;
}

// Shared by the pay card and JobPosting JSON-LD so the two can't diverge. Null
// when there's no positive hourly figure (no/zero duration, or rounds to $0/hr).
export function sessionRateToHourly(
  amount: number,
  durationMins: number | null | undefined,
): number | null {
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  if (!durationMins || durationMins <= 0) {
    return null;
  }
  const hourly = Math.round(amount / (durationMins / 60));
  return hourly > 0 ? hourly : null;
}

const WHOLE_DOLLARS = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});
const DOLLARS_AND_CENTS = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * A rate as a dollar figure: whole dollars where the amount is whole, cents
 * where it carries them, thousands grouped ("$1,500").
 *
 * Every figure here used to be rounded, which turned the $412.50 an expert is
 * actually paid into "$413" on every public surface while their own handbook
 * printed the exact rate. Two roundings of one number, on two pages they can
 * have open side by side, is a disagreement about money. Listings priced in
 * whole dollars -- nearly all of them -- read exactly as they did.
 *
 * The one figure still rounded is `sessionRateToHourly`'s, which is a derived
 * equivalent rather than a rate anybody is paid.
 */
function formatDollars(amount: number): string {
  return `$${(Number.isInteger(amount) ? WHOLE_DOLLARS : DOLLARS_AND_CENTS).format(amount)}`;
}

/**
 * Canonical pay presentation shared by the opportunity listing UI and applicant
 * emails. `convertSessionToHourly` is the listing-card behavior (a per-session
 * rate is shown as its hourly equivalent when the duration is known); emails
 * pass false so the figure stays equal to what the applicant is actually paid
 * for one unit of work. Returns null when there is no positive amount.
 */
export function formatPayDisplay(input: {
  amount: string | number | null;
  payRateFrequency: string | null | undefined;
  category?: string | null;
  estimatedDurationMins?: number | null;
  convertSessionToHourly?: boolean;
}): PayDisplay | null {
  const base = Number(input.amount);
  if (!Number.isFinite(base) || base <= 0) {
    return null;
  }

  if (input.payRateFrequency === "HOURLY") {
    return {
      amount: formatDollars(base),
      suffix: "/ hr",
      label: "per hour",
    };
  }
  // ONE_TIME/PER_TASK are Study.payRateFrequency (the domain truth); FIXED/PER_SESSION are deprecated listing values kept until every surface reads the study.
  if (
    input.payRateFrequency === "ONE_TIME" ||
    input.payRateFrequency === "FIXED"
  ) {
    return {
      amount: formatDollars(base),
      suffix: "one-time",
      label: "one-time",
    };
  }
  if (input.payRateFrequency === "PER_TASK") {
    return {
      amount: formatDollars(base),
      suffix: "/ task",
      label: "per task",
    };
  }

  if (input.convertSessionToHourly) {
    const hourly = sessionRateToHourly(base, input.estimatedDurationMins);
    if (hourly != null) {
      return {
        amount: formatDollars(hourly),
        suffix: "/ hr",
        label: "per hour",
      };
    }
  }

  const session = formatDollars(base);
  if (input.category === "PROJECT_BASED") {
    return { amount: session, suffix: "/ task", label: "per task" };
  }
  if (input.category === "ONE_TIME") {
    return { amount: session, suffix: "one-time", label: "one-time" };
  }
  return { amount: session, suffix: "", label: "per session" };
}

/**
 * Pay shown in applicant emails: the amount paid for one unit of work plus its
 * cadence. Unlike the listing card it never converts a per-session rate into an
 * hourly equivalent, so the "Earn $X" CTA stays equal to the actual payout.
 */
export function formatEmailPay(input: {
  amount: string | number | null;
  payRateFrequency: string | null | undefined;
  category?: string | null;
}): { payRate: string; payCadence: string } | null {
  const display = formatPayDisplay({
    amount: input.amount,
    payRateFrequency: input.payRateFrequency,
    category: input.category,
    convertSessionToHourly: false,
  });
  if (!display) {
    return null;
  }
  return {
    payRate: display.amount,
    payCadence: display.suffix || display.label,
  };
}

/** "$45/hour", "$150 per task", "$100": the figure and its unit for a sentence; null at $0. */
export function formatPayInline(input: {
  amount: string | number | null | undefined;
  payRateFrequency: string | null | undefined;
}): string | null {
  const display = formatPayDisplay({
    amount: input.amount ?? null,
    payRateFrequency: input.payRateFrequency,
  });
  if (!display) {
    return null;
  }
  if (input.payRateFrequency === "HOURLY") {
    return `${display.amount}/hour`;
  }
  if (input.payRateFrequency === "PER_TASK") {
    return `${display.amount} per task`;
  }
  return display.amount;
}

/** A referrer's cut of one unit of the referred person's pay, to the cent. */
export function referralEstimateUsd(rateUsd: number, bps: number): number {
  return Math.round((rateUsd * bps) / 100) / 100;
}

/** That cut as a dollar figure, cents only where it has them; null at $0. */
export function formatReferralEstimateUsd(
  rateUsd: number,
  bps: number,
): string | null {
  const estimateUsd = referralEstimateUsd(rateUsd, bps);
  return estimateUsd > 0 ? formatDollars(estimateUsd) : null;
}
